// Need to use the React-specific entry point to allow generating React hooks
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {Toast} from '@zoom/react-ui';
import {TFunction} from 'i18next';

import {defaultHeaders} from 'Api/ApiUtils';
import {attachScope, getBatchMeetingSettings, getZCalToken} from 'Api/ZCalendar';
import {snackbarActions} from 'Store/SnackbarStore';
import {RFResponse} from 'types/routing';
import {getVirtualUserEmail, strEq, virtualUserEmailToUid} from 'Utils';
import {PAY_CHANNEL, PAYMENT_INTEGRATION_TYPE, SCOPE_TYPE} from 'Utils/consts';
import {getAid} from 'Utils/integration';

const useProxy =
  process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

let zschedulerURL = '/zscheduler/v1/';
if (useProxy) {
  // use proxy on dev env for cors issue
  zschedulerURL = '/api/zscheduler/v1/';
}

export {zschedulerURL};
// Define a service using a base URL and expected endpoints
export const hostApi = createApi({
  reducerPath: 'hostApi',
  baseQuery: fetchBaseQuery({
    baseUrl: zschedulerURL,
    prepareHeaders: async (headers: Headers) => {
      const token = await getZCalToken();
      Object.entries(defaultHeaders).forEach(([k, v]) =>
        headers.set(k, v),
      );
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['OrgMemberList', 'Group', 'GroupMemberList', 'GroupMember',
    'Team', 'TeamMember', 'TeamMemberList', 'TeamMembershipsList',
    'Workflow', 'WorkflowList', 'WorkflowTemplateMessageList',
    'RoutingFormResponseList'],
  endpoints: (builder) => ({
    getAppt: builder.query<Appt, {userId?: string, apptId: string}>({
      query: (arg: {userId: string, apptId: string}) => {
        const q: any = {};
        if (arg.userId) {
          q.user = arg.userId;
        }
        return {
          url: `appointments/${arg.apptId}`,
          params: q,
        };
      },
    }),
    listOrgUsers: builder.query({
      query: (q) => {
        return {
          url: `organization/members`,
          params: q,
        };
      },
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.items ?? []).map(({id}) => ({type: 'OrgMember', id})),
            {type: 'OrgMemberList', id: arg},
            'OrgMemberList',
          ] :
          [{type: 'OrgMemberList', id: arg}, 'OrgMemberList'],
    }),
    getUserByUid: builder.query({
      query: (userId) => `organization/member/${userId}`,
      transformResponse: (response: any, meta: any, arg: any): object => response.items[0],
    }),
    listWorkflowTemplateMessages: builder.query({
      query: (lang: string) => {
        const q: any = {};
        if (lang) {
          q.lang = lang;
        }
        return {
          url: `workflows/templates/messages`,
          params: q,
        };
      },
      providesTags: (result, error, arg) => [
        {type: 'WorkflowTemplateMessageList', id: arg},
      ],
    }),
    listWorkflowsForUser: builder.query({
      query: (scope: Scope) => {
        return {
          url: `workflows`,
          params: attachScope(scope),
        };
      },
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.items ?? []).map(({id}) => ({type: 'Workflow', id})),
            {type: 'WorkflowList', id: arg},
          ] :
          [{type: 'WorkflowList', id: arg}],
    }),
    listGroups: builder.query({
      query: () => `group`,
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.groups ?? []).map(({id}) => ({type: 'Group', id})),
            {type: 'Group', id: 'LIST'},
          ] :
          [{type: 'Group', id: 'LIST'}],
    }),
    listGroupMembers: builder.query({
      query: (groupId: string) => `group/${groupId}/members`,
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.members ?? []).map(({id}) => ({type: 'GroupMember', id})),
            {type: 'GroupMemberList', id: arg},
            'GroupMemberList',
          ] :
          [{type: 'GroupMemberList', id: arg}, 'GroupMemberList'],
    }),
    listTeams: builder.query({
      query: () => `team`,
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.teams ?? []).map(({id}) => ({type: 'Team', id})),
            {type: 'Team', id: 'LIST'},
          ] :
          [{type: 'Team', id: 'LIST'}],
      transformResponse: (response: any) => {
        response.teams = response.teams.filter((team: any) => !team.id.startsWith('ocsub.'));
        return response;
      },
    }),
    listMemberTeams: builder.query({
      query: (userId: string) => {
        const q: any = {};
        if (userId) {
          q.userId = userId;
        }
        return {
          url: `team`,
          params: q,
        };
      },
      transformResponse: (response: any) => {
        response.teams = response.teams.filter((team: any) => !team.id.startsWith('ocsub.'));
        return response;
      },
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.teams ?? []).map(({id}) => ({type: 'Team', id})),
            {type: 'TeamMembershipsList', id: arg},
            'TeamMembershipsList',
          ] :
          [{type: 'TeamMembershipsList', id: arg}, 'TeamMembershipsList'],
    }),
    listTeamMembers: builder.query({
      query: (teamId: string) => `team/${teamId}/members`,
      providesTags: (result, error, arg) =>
        result ?
          [
            ...(result.members ?? []).map(({id}) => ({type: 'TeamMember', id})),
            {type: 'TeamMemberList', id: arg},
            'TeamMemberList',
          ] :
          [{type: 'TeamMemberList', id: arg}, 'TeamMemberList'],
    }),
    searchOrgUsers: builder.query({
      query: (query) => {
        return {
          url: 'organization/members/search',
          params: query,
        };
      },
    }),
    listRoutingFormResponses: builder.query<{
      responses: RFResponse[];
      nextPageToken: string|null|undefined;
    }, {
      formId: string;
      userId: string;
      pageToken: string|null|undefined;
    }
    >({
      query: ({formId, pageToken, userId}) => {
        const q: any = {};
        if (userId) {
          q.userId = userId;
        }
        if (pageToken) {
          q.pageToken = pageToken;
        }
        q.maxResults = 10;

        return {
          url: `routing/forms/${formId}/response`,
          params: q,
        };
      },
      providesTags: (result, error, arg) => [
        {type: 'RoutingFormResponseList', id: arg.formId},
      ],
      serializeQueryArgs: ({endpointName, queryArgs}) => {
        return `${endpointName}-${queryArgs.formId}-${queryArgs.userId}`;
      },
      // Always merge incoming data to the cache entry
      merge: (currentCache, respData, otherArgs) => {
        // A new request for page 0 was made; don't merge with previous pages
        if (!otherArgs.arg.pageToken) {
          return respData;
        }
        currentCache.responses.push(...respData.responses);
        currentCache.nextPageToken = respData.nextPageToken;
      },
      // Refetch when the page arg changes
      forceRefetch({currentArg, previousArg}) {
        return currentArg?.pageToken !== previousArg?.pageToken;
      },
    }),
    getUserSettingsSlug: builder.query({
      query: (userId: string) => {
        const q: any = {};
        if (userId) {
          q.userId = userId;
        }
        return {
          url: `settings/slug`,
          params: q,
        };
      },
    }),
    batchGetSettings: builder.query({
      queryFn: async (attendees: FormStateHostAttendee[], baseQueryApi) => {
        const attendeeUids = attendees.filter((att) => att.host).map((att) => virtualUserEmailToUid(att.email));
        const meetingSettings = await getBatchMeetingSettings(attendeeUids);
        // Pre-fill the individual meeting setting entries with the results
        // from the batch endpoint query
        baseQueryApi.dispatch(
          hostApi.util.upsertQueryEntries(
            Object.entries(meetingSettings.data).map(([uid, setting]) => {
              return ({
                endpointName: 'getUserMeetingSettings',
                arg: {type: SCOPE_TYPE.USER, id: uid.toLowerCase()},
                value: setting,
              });
            }),
          ),
        );
        return {data: true};
      },
    }),
    getUserAvailabilitySchedules: builder.query({
      query: (scope: Scope) => {
        const q: any = {};
        if (scope) {
          q.user = getVirtualUserEmail(scope.id);
        }
        return {
          url: `availability`,
          params: q,
        };
      },
    }),
    getMeetIntegration: builder.query({
      query: ({service, userEmail}: {
        service: 'googleMeet'|'teamMeet',
        userEmail: string,
      }) => {
        const queryString = new URLSearchParams({user: userEmail});
        return `integration/${service}?${queryString.toString()}`;
      },
    }),
    getUserMeetingSettings: builder.query({
      query: (scope: Scope) => {
        const q: any = {};
        if (scope) {
          q.user = scope.id;
        }
        return {
          url: `meetingSettings`,
          params: q,
        };
      },
      transformResponse: (response: {data: {[uid: string]: ZmMeetingSettings}}): ZmMeetingSettings => {
        const userMeetingSettings = Object.values(response.data)[0];
        return userMeetingSettings;
      },
    }),
    getUsersBusyEvents: builder.query({
      query: ({
        timeMax,
        timeMin,
        users,
      }: {
        timeMin: string,
        timeMax: string,
        users: string[],
      }) => {
        const queryParams = new URLSearchParams({
          timeMin,
          timeMax,
        });

        let queryWithUsers = queryParams.toString();
        if (users) {
          const queryString = users.map((user) => `user=${user.toLowerCase()}`).join('&');
          queryWithUsers += `&${queryString}`;
        }
        return `events/busy?${queryWithUsers}`;
      },
      transformResponse: (response: any, meta, arg): object => {
        const eventsMap: Record<string, any> = {};
        response.items.forEach((item: any) => {
          const email = item.host;
          const uId = email.substring(0, email.indexOf('@'));
          // TODO don't add if uppercase
          // if (!events.has(item.host)) {
          // Don't add if not in the queried hosts
          if (!arg.users || arg.users.some((calendarId) => strEq(calendarId, email))) {
            eventsMap[uId] = item.value;
          }
        // }
        });
        return eventsMap;
      },
    }),
    /**
     * Noop 'mutation' to trigger refetching scope data by invalidating resource tags
     *  for groups, teams, group members, org members
     */
    refetchScopes: builder.mutation({
      queryFn: () => ({
        data: null,
      }),
      invalidatesTags: ['Team', 'TeamMembershipsList', 'Group', 'GroupMemberList', 'OrgMemberList'],
    }),
    invalidateWorkflows: builder.mutation({
      queryFn: () => ({
        data: null,
      }),
      invalidatesTags: ['Workflow', 'WorkflowList'],
    }),
    getFeatureToggles: builder.query({
      query: (toggles: Array<{configKey: string, configType: number}>) => ({
        url: 'featureToggle',
        method: 'POST',
        body: toggles,
      }),
      transformResponse: (response: Array<Record<string, any>> | Record<string, any>): Record<string, any> => {
        // Handle both array and object responses
        if (Array.isArray(response)) {
          // Flatten the array of objects into a single object
          return response.reduce((acc, item) => ({...acc, ...item}), {});
        } else if (response && typeof response === 'object') {
          // Response is already a flattened object
          return response;
        } else {
          // Fallback for unexpected formats
          return {};
        }
      },
    }),
    getDefaultCurrency: builder.query({
      query: () => 'settings/defaultCurrency',
      transformResponse: (response: {value: string}) => response.value,
    }),
    updateDefaultCurrency: builder.mutation({
      query: (currency: string) => ({
        url: 'settings/defaultCurrency',
        method: 'PATCH',
        body: {id: 'defaultCurrency', value: currency},
      }),
      async onQueryStarted(currency, {dispatch, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          dispatch(
            hostApi.util.updateQueryData('getDefaultCurrency', undefined, () => data.value),
          );
        } catch (error) {
          dispatch(snackbarActions.addSnackbar({
            message: 'integration.genericSettingOpFailed',
            severity: 'warning',
          }));
        }
      },
    }),
    getPaymentIntegration: builder.query({
      query: (service: string) => {
        let searchParams = '';
        if (service === PAYMENT_INTEGRATION_TYPE.STRIPE) {
          searchParams = new URLSearchParams({paymentProcessor: `${PAY_CHANNEL.STRIPE}`}).toString();
        }
        return `billing/onboarding?${searchParams}`;
      },
      // Currently, one zoom account can only connect at most one payment account
      transformResponse: (response: {items: any[]}) => ({...(response.items?.[0])}),
    }),
    connectPaymentIntegration: builder.mutation({
      query: ({code, service, state}: {code: string, service: string, state: string}) => ({
        url: 'billing/onboarding',
        method: 'PATCH',
        body: {
          payChannel: PAY_CHANNEL.STRIPE,
          payScene: 1, // web payment
          code,
          state,
        },
      }),
      async onQueryStarted({service}, {dispatch, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          dispatch(
            hostApi.util.updateQueryData('getPaymentIntegration', service, () => data),
          );
          dispatch(snackbarActions.addSnackbar({
            message: 'integration.connectSuccess',
            severity: 'success',
          }));
        } catch (error) {
          dispatch(snackbarActions.addSnackbar({
            message: 'integration.connectFailed',
            severity: 'warning',
          }));
        }
      },
    }),
    disconnectPaymentIntegration: builder.mutation({
      query: (arg: {service: string, t: TFunction}) => {
        const data: any = {};
        if (arg.service === PAYMENT_INTEGRATION_TYPE.STRIPE) {
          data.payChannel = PAY_CHANNEL.STRIPE;
        }
        return {
          url: 'billing/onboarding',
          method: 'DELETE',
          body: data,
        };
      },
      async onQueryStarted({service, t}, {dispatch, queryFulfilled}) {
        try {
          await queryFulfilled;
          dispatch(
            hostApi.util.updateQueryData('getPaymentIntegration', service, () => ({})),
          );
          Toast.config({placement: 'top-end'});
          Toast.success({
            title: t('payment.accountDisconnected') as string,
            content: t('payment.accountDisconnectedDesc', {service}),
            duration: 5000,
            closable: true,
          });
        } catch (error) {
          dispatch(snackbarActions.addSnackbar({
            message: 'integration.disconnectFailed',
            severity: 'warning',
          }));
        }
      },
    }),
    patchOrganizationSetting: builder.mutation({
      query: (data: any) => {
        return {
          url: `organization/${getAid()}`,
          method: 'PATCH',
          body: data,
        };
      },
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useConnectPaymentIntegrationMutation,
  useDisconnectPaymentIntegrationMutation,
  useGetApptQuery,
  useGetDefaultCurrencyQuery,
  useGetFeatureTogglesQuery,
  useGetMeetIntegrationQuery,
  useGetPaymentIntegrationQuery,
  useGetUserAvailabilitySchedulesQuery,
  useGetUserByUidQuery,
  useGetUserMeetingSettingsQuery,
  useGetUserSettingsSlugQuery,
  useGetUsersBusyEventsQuery,
  useInvalidateWorkflowsMutation,
  useLazyBatchGetSettingsQuery,
  useLazyGetUserMeetingSettingsQuery,
  useLazyListWorkflowTemplateMessagesQuery,
  useLazySearchOrgUsersQuery,
  useListGroupMembersQuery,
  useListGroupsQuery,
  useListMemberTeamsQuery,
  useListOrgUsersQuery,
  useListRoutingFormResponsesQuery,
  useListTeamMembersQuery,
  useListTeamsQuery,
  useListWorkflowTemplateMessagesQuery,
  useListWorkflowsForUserQuery,
  usePatchOrganizationSettingMutation,
  useRefetchScopesMutation,
  useUpdateDefaultCurrencyMutation,
} = hostApi;
